import type { MenuDataItem } from '@ant-design/pro-components';
import type { RouteAccess } from '@/access';

const purchaseRoot = '/purchase-order-management';
const analysisRoot = '/procurement-analysis';

export const procurementMenuGroups = [
  {
    key: 'quotes-orders',
    paths: [
      `${purchaseRoot}/offers`,
      `${purchaseRoot}/pending-approval-products`,
      `${purchaseRoot}/submitted-products`,
      `${purchaseRoot}/orders`,
      `${purchaseRoot}/modification-approval`,
      `${purchaseRoot}/ai-purchase-import`,
    ],
  },
  {
    key: 'arrival-replenishment',
    paths: [
      `${purchaseRoot}/arrival-schedule`,
      `${analysisRoot}/replenishment`,
      `${analysisRoot}/category-arrival-calendar`,
    ],
  },
  {
    key: 'payments-finance',
    paths: [
      `${purchaseRoot}/payment-management`,
      `${purchaseRoot}/payment-variance-review`,
      `${purchaseRoot}/logistics-fee-management`,
      `${purchaseRoot}/order-payment-followup`,
      `${purchaseRoot}/finance-management`,
      `${purchaseRoot}/payment-vouchers`,
    ],
  },
  { key: 'goals', paths: [`${purchaseRoot}/goals`] },
  {
    key: 'market-pricing',
    paths: [
      `${purchaseRoot}/market-price-search`,
      `${purchaseRoot}/greenprice-catalog`,
      `${purchaseRoot}/greenprice-whatsapp`,
      `${purchaseRoot}/price-competitiveness`,
    ],
  },
  {
    key: 'analysis-review',
    paths: [
      `${analysisRoot}/dashboard`,
      `${analysisRoot}/purchase-analysis`,
      `${analysisRoot}/purchase-history-performance`,
      `${analysisRoot}/opportunity-analytics`,
      `${analysisRoot}/small-trial-sale-followup`,
      `${analysisRoot}/purchase-data-errors`,
    ],
  },
] as const;

type ProcurementPath = (typeof procurementMenuGroups)[number]['paths'][number];
const landingAccess: Record<ProcurementPath, keyof RouteAccess> = {
  [`${purchaseRoot}/offers`]: 'canViewPurchaseQuote',
  [`${purchaseRoot}/pending-approval-products`]: 'canApprovePurchaseQuote',
  [`${purchaseRoot}/submitted-products`]: 'canViewPurchaseQuote',
  [`${purchaseRoot}/orders`]: 'canViewPurchaseOrder',
  [`${purchaseRoot}/modification-approval`]: 'canViewPurchaseOrderModifyApproval',
  [`${purchaseRoot}/ai-purchase-import`]: 'canViewAiPurchaseImport',
  [`${purchaseRoot}/arrival-schedule`]: 'canViewPurchaseOrderArrivalSchedule',
  [`${analysisRoot}/replenishment`]: 'canViewReplenishment',
  [`${analysisRoot}/category-arrival-calendar`]: 'canViewPurchaseCategoryArrivalCalendar',
  [`${purchaseRoot}/payment-management`]: 'canViewPurchaseOrderPayment',
  [`${purchaseRoot}/payment-variance-review`]: 'canViewPurchaseOrderPaymentVarianceReview',
  [`${purchaseRoot}/logistics-fee-management`]: 'canViewLogisticsFeeManagement',
  [`${purchaseRoot}/order-payment-followup`]: 'canViewPurchaseOrderHandlingFollowup',
  [`${purchaseRoot}/finance-management`]: 'canViewPurchaseOrderFinanceManagement',
  [`${purchaseRoot}/payment-vouchers`]: 'canViewPurchaseOrderPaymentVoucher',
  [`${purchaseRoot}/goals`]: 'canViewProcurementGoals',
  [`${purchaseRoot}/market-price-search`]: 'canViewMarketPriceSearch',
  [`${purchaseRoot}/greenprice-catalog`]: 'canViewGreenpriceCatalog',
  [`${purchaseRoot}/greenprice-whatsapp`]: 'canViewGreenpriceCatalog',
  [`${purchaseRoot}/price-competitiveness`]: 'canViewPriceCompetitiveness',
  [`${analysisRoot}/dashboard`]: 'canViewProcurementDashboard',
  [`${analysisRoot}/purchase-analysis`]: 'canViewPurchaseAnalysis',
  [`${analysisRoot}/purchase-history-performance`]: 'canViewPurchaseHistoryPerformance',
  [`${analysisRoot}/opportunity-analytics`]: 'canViewPurchaseOpportunityAnalytics',
  [`${analysisRoot}/small-trial-sale-followup`]: 'canViewSmallTrialSaleFollowup',
  [`${analysisRoot}/purchase-data-errors`]: 'canViewPurchaseDataErrors',
};

export function getAccessibleProcurementLandingPage(access: RouteAccess): string | null {
  for (const { paths } of procurementMenuGroups)
    for (const path of paths) if (access[landingAccess[path]]) return path;
  return null;
}

/** Reshape the ProLayout menu only; the route tree, URL and route access stay authoritative. */
export function groupProcurementMenu(
  menu: MenuDataItem[],
  groupLabel: (key: (typeof procurementMenuGroups)[number]['key']) => string,
  rootLabel?: string,
): MenuDataItem[] {
  const purchase = menu.find((item) => item.path === purchaseRoot);
  const analysis = menu.find((item) => item.path === analysisRoot);
  if (!purchase && !analysis) return menu;

  const visible = [...(purchase?.children || []), ...(analysis?.children || [])].filter(
    (item) => !item.hideInMenu && !item.redirect,
  );
  if (!visible.length)
    return menu.filter((item) => item.path !== purchaseRoot && item.path !== analysisRoot);
  const byPath = new Map(visible.map((item) => [item.path, item]));
  const assigned = new Set<string>();
  const groups = procurementMenuGroups.flatMap(({ key, paths }) => {
    const children = paths
      .map((path) => byPath.get(path))
      .filter((item): item is MenuDataItem => Boolean(item))
      .map((item) => ({ ...item, locale: false as const }));
    children.forEach((item) => item.path && assigned.add(item.path));
    if (!children.length) return [];
    if (key === 'goals') return children;
    return [
      {
        key: `${purchaseRoot}/group-${key}`,
        path: `${purchaseRoot}/group-${key}`,
        name: groupLabel(key),
        locale: false as const,
        children,
      },
    ];
  });
  const remaining = visible.filter((item) => !item.path || !assigned.has(item.path));
  const combined: MenuDataItem = {
    ...(purchase || analysis),
    path: purchaseRoot,
    name: rootLabel || purchase?.name || analysis?.name,
    locale: rootLabel ? false : purchase?.locale,
    children: [...groups, ...remaining],
  };
  return menu.flatMap((item) => {
    if (item.path === analysisRoot) return purchase ? [] : [combined];
    if (item.path === purchaseRoot) return [combined];
    return [item];
  });
}
